import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select } from 'chakra-react-select';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import {
  AssessmentOutcomeSchema,
  type AssessmentOutcome,
} from '@duke-hq/svc-api-db/schemas/enums/AssessmentOutcome.schema';

type AssessmentOutcomeProps = {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: { outcome: AssessmentOutcome; override?: string }) => void;
  assessmentSubmission: RouterOutputs['assessments']['getById'];
  onSubmitWithConfirmation?: (data: { outcome: AssessmentOutcome; override?: string }) => void;
};

type AssessmentOutcomeSelectOption = {
  label: string;
  value: AssessmentOutcome;
};

interface AssessmentOutcomeFormProps {
  outcome: AssessmentOutcome | null;
  overrideReason?: string | null;
}

const AssessmentOutcomeOptions = [
  { label: 'Pending', value: AssessmentOutcomeSchema.enum.PENDING },
  {
    label: 'Not yet satisfactory',
    value: AssessmentOutcomeSchema.enum.NOT_YET_SATISFACTORY,
  },
  {
    label: 'Override: Satisfactory',
    value: AssessmentOutcomeSchema.enum.SATISFACTORY,
  },
];

export function AssessmentOutcomeModal({
  isOpen,
  onClose,
  onUpdate,
  assessmentSubmission,
  onSubmitWithConfirmation,
}: AssessmentOutcomeProps) {
  const defaultFormState: AssessmentOutcomeFormProps = useMemo(
    () => ({
      outcome: assessmentSubmission.outcome,
      overrideReason: '',
    }),
    [assessmentSubmission.outcome],
  );

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      ...defaultFormState,
    },
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const watchedOutcome = watch('outcome'); // Watch the outcome field

  const isOutcomeChanged = useMemo(
    () => watchedOutcome !== defaultFormState.outcome,
    [watchedOutcome, defaultFormState],
  );

  const onSubmit = (data: AssessmentOutcomeFormProps) => {
    if (data.outcome === null) {
      return;
    }

    const formattedData = {
      outcome: data.outcome,
      overrideReason:
        data.overrideReason != null && data.overrideReason !== '' ? data.overrideReason : undefined,
    };

    // If onSubmitWithConfirmation is provided, use it (for cases requiring confirmation)
    // Otherwise, call onUpdate directly
    if (onSubmitWithConfirmation) {
      onSubmitWithConfirmation(formattedData);
    } else {
      onUpdate(formattedData);
    }
  };

  return (
    <Modal
      size="2xl"
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent
        as={'form'}
        padding={4}
        minH={'300px'}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <ModalHeader>
          <Trans>Update assessment outcome</Trans>
          <ModalCloseButton margin={4} />
        </ModalHeader>

        <ModalBody pb={6} h="full">
          <FormProvider {...methods}>
            <VStack gap="8px" w="full" py="8px">
              <HStack w="full" justify="space-between" align="start" gap="10px">
                <FormControl isInvalid={!!errors.outcome} isRequired>
                  <FormLabel htmlFor="outcome" fontWeight="medium" fontSize="sm">
                    <Trans>Assessment outcome</Trans>
                  </FormLabel>
                  <Controller
                    name="outcome"
                    control={control}
                    defaultValue={assessmentSubmission.outcome}
                    render={({ field }) => (
                      <Select
                        id="outcome"
                        placeholder={t`Please select`}
                        options={AssessmentOutcomeOptions.map((option) => ({
                          ...option,
                          isDisabled: option.value === field.value, // Disable the default selected option
                        }))}
                        onChange={(val: { value: string; label: string }) =>
                          field.onChange(val?.value)
                        }
                        value={AssessmentOutcomeOptions.find(
                          (option: AssessmentOutcomeSelectOption) => option.value === field.value,
                        )}
                      />
                    )}
                    rules={{ required: t`Assessment outcome is required.` }}
                  />
                  <FormErrorMessage>{errors.outcome?.message ?? null}</FormErrorMessage>
                </FormControl>
              </HStack>
            </VStack>

            {isOutcomeChanged ? (
              <>
                {(() => {
                  switch (watchedOutcome) {
                    case AssessmentOutcomeSchema.enum.SATISFACTORY:
                      return (
                        <VStack>
                          <FormControl isInvalid={!!errors.overrideReason} isRequired>
                            <FormLabel htmlFor="overrideReason">
                              <Trans>Reason for this override</Trans>
                            </FormLabel>
                            <Textarea
                              id="overrideReason"
                              placeholder={t`Add a reason for this override`}
                              height="auto"
                              rows={5}
                              mb={2}
                              size="sm"
                              {...register('overrideReason', {
                                required: t`Please provide a reason for this override.`,
                              })}
                            />
                            <FormErrorMessage>
                              {errors.overrideReason?.message ?? null}
                            </FormErrorMessage>
                          </FormControl>
                        </VStack>
                      );
                    case AssessmentOutcomeSchema.enum.NOT_YET_SATISFACTORY:
                      return (
                        <Box as="p" fontSize="sm" color="fg.muted">
                          <Trans>
                            This will change the outcome of the assessment and could affect
                            availability of course content for the student.
                          </Trans>
                        </Box>
                      );
                    case AssessmentOutcomeSchema.enum.PENDING:
                      return (
                        <Box as="p" fontSize="sm" color="fg.muted">
                          <Trans>
                            This will remove the current outcome. Individual question assessments
                            and feedback will be retained.
                          </Trans>
                        </Box>
                      );
                    case AssessmentOutcomeSchema.enum.WITHDRAWN:
                      return (
                        <Box as="p" fontSize="sm" color="fg.muted">
                          <Trans>This assessment has been withdrawn and cannot be modified.</Trans>
                        </Box>
                      );
                    case null:
                      return null;
                    default:
                      return null;
                  }
                })()}
              </>
            ) : null}
          </FormProvider>
        </ModalBody>
        <ModalFooter gap={4}>
          <Button type="button" variant="ghost" colorScheme="black" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button type="submit" isDisabled={!isOutcomeChanged}>
            <Trans>Update outcome</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
